/**
 * The conversation surface: speak, or choose.
 *
 * The design rule the locked plan insists on, and which shapes everything here: **the authored
 * options are always on screen and always clickable.** Voice is layered on top of a working
 * conversation, never in front of it. A player whose browser has no speech engine, whose
 * microphone is refused, who is in a room where they cannot talk, or who was simply misheard,
 * plays the same game — they click. There is no path through this panel that can dead-end.
 *
 * So the flow is:
 *
 *   hold to transmit → transcript → matcher → matched? advance : say so, options still there
 *
 * and every arrow can fail without consequence.
 */
import { h } from '../dom.js';
import { t } from '../../i18n/index.js';
import { api } from '../../api/client.js';
import type { Investigation, InvestigationView } from '../../story/investigation.js';
import type { SpeechPlayer } from '../../audio/speech.js';
import type { PushToTalk, TransmitOutcome } from '../../voice/index.js';
import type { TransmitState } from '../../voice/index.js';

export interface DialoguePanelOptions {
  investigation: Investigation;
  /** Announce clues gained by answering, the same way tuning into one does. */
  onClues: (clueIds: string[]) => void;
  /** Absent when speech is unavailable — the panel is then simply the buttons. */
  pushToTalk?: PushToTalk | null;
  /**
   * Offer the recorded fragment to the ghost relay. Absent when the relay is unavailable.
   *
   * The same held key both answers the NPC and leaks onto the band, which is the point: a player
   * transmits once, and other people on that frequency may catch it. Nothing extra is asked of
   * them, and there is no separate "broadcast" button — that would make it a chat feature.
   */
  onFragment?: ((sttText: string | undefined) => void) | null;
  /**
   * Plays the authored reading of each line. Absent when there is no audio stack at all; a node
   * with no recording behaves the same way, so the conversation never depends on it.
   */
  speech?: SpeechPlayer | null;
  /** Whether to render a dedicated voiceRow inside the panel. Default true for compatibility. */
  showVoiceRow?: boolean;
}

export interface DialoguePanel {
  element: HTMLElement;
  dispose(): void;
}

export function createDialoguePanel(options: DialoguePanelOptions): DialoguePanel {
  const { investigation, onClues, pushToTalk, onFragment, speech } = options;

  // Which node has already been read aloud. Without this the clip restarts on every view push —
  // and the view is recomputed whenever the save changes, which includes the player's own dial.
  let spokenNodeId: string | null = null;

  const line = h('p', { class: 'nb-dialogue__line', 'aria-live': 'polite' });
  const optionList = h('div', { class: 'nb-dialogue__options' });

  /**
   * Say out loud that speaking is optional.
   *
   * The written replies have always worked, since Chapter 4 — but a player who cannot speak, will
   * not speak, or is in a room where speaking is impossible has had to *infer* that from the
   * buttons being there. Inferring it is not the same as being told, and a game whose headline
   * verb is "talk to it" owes the other path a sentence rather than a hope.
   */
  const inputHint = h('p', { class: 'nb-dialogue__hint', text: t('dialogue.inputHint') });

  /* ---- voice surface --------------------------------------------------------------------- */

  // What the set is doing right now: heard text while transmitting, then the outcome.
  const heard = h('p', { class: 'nb-transmit__heard', 'aria-live': 'polite' });
  const status = h('p', { class: 'nb-transmit__status', role: 'status', 'aria-live': 'polite' });

  const talkButton = h('button', {
    class: 'nb-button nb-transmit__key',
    type: 'button',
    text: t('transmit.hold'),
  }) as HTMLButtonElement;

  const meter = h('span', { class: 'nb-transmit__meter', 'aria-hidden': 'true' });
  const meterFill = h('span', { class: 'nb-transmit__fill' });
  meter.appendChild(meterFill);

  const voiceRow = h(
    'div',
    { class: 'nb-transmit' },
    talkButton,
    meter,
    h('div', { class: 'nb-transmit__readout' }, heard, status),
  );

  let matching = false;
  let countdown: ReturnType<typeof setInterval> | null = null;

  const setStatus = (message: string, kind: 'idle' | 'busy' | 'warn' = 'idle'): void => {
    status.textContent = message;
    status.dataset.kind = kind;
  };

  const renderTransmitState = (state: TransmitState): void => {
    talkButton.dataset.phase = state.phase;
    talkButton.disabled = matching || (!state.canTransmit && state.phase !== 'transmitting');

    if (state.phase === 'transmitting') {
      const total = pushToTalk?.rules.limits.maxTransmitMs ?? 1;
      meterFill.style.width = `${Math.round((state.remainingMs / total) * 100)}%`;
      meter.dataset.active = 'true';
      talkButton.textContent = t('transmit.transmitting');
      return;
    }

    meter.dataset.active = 'false';
    meterFill.style.width = '0%';

    if (state.phase === 'cooldown' || state.phase === 'overheated') {
      const seconds = Math.ceil(state.waitMs / 1000);
      talkButton.textContent =
        state.phase === 'overheated'
          ? t('transmit.overheated', { seconds })
          : t('transmit.cooling', { seconds });
      return;
    }

    talkButton.textContent = matching ? t('transmit.matching') : t('transmit.hold');
  };

  /** Keep the countdown ticking while the set is unavailable, so the wait is legible. */
  const startCountdown = (): void => {
    if (countdown !== null || !pushToTalk) return;
    countdown = setInterval(() => {
      const state = pushToTalk.state();
      renderTransmitState(state);
      if (state.phase === 'idle' && countdown !== null) {
        clearInterval(countdown);
        countdown = null;
      }
    }, 200);
  };

  /**
   * A finished transmission. Everything here ends with the options still on screen — the only
   * difference between success and every kind of failure is whether the tree advanced.
   */
  const handleOutcome = async (outcome: TransmitOutcome): Promise<void> => {
    if (outcome.kind === 'refused') {
      setStatus(t('transmit.tooSoon'), 'warn');
      startCountdown();
      return;
    }

    if (outcome.kind === 'error') {
      // Told once, plainly, and then the player carries on with the buttons.
      setStatus(t(`transmit.error.${outcome.error.kind}` as never), 'warn');
      return;
    }

    if (outcome.kind === 'silent') {
      // Nothing was understood, but something may still have been said — the fragment is offered
      // to the relay regardless. Half of what makes a ghost eerie is that it is not addressed to
      // anyone and does not resolve into words.
      onFragment?.(undefined);
      setStatus(t('transmit.nothingHeard'), 'warn');
      startCountdown();
      return;
    }

    // The transcript goes to the relay for moderation screening only; the audio goes with it.
    onFragment?.(outcome.transcript);

    heard.textContent = t('transmit.heard', { text: outcome.transcript });

    const current = investigation.view.get().dialogue;
    if (!current) return;

    matching = true;
    setStatus(t('transmit.matching'), 'busy');
    renderTransmitState(pushToTalk!.state());

    try {
      const result = await api.matchUtterance(
        outcome.transcript,
        current.node.options.map((option) => ({
          optionId: option.id,
          label: option.label,
          ...(option.intents.length > 0 ? { intents: option.intents } : {}),
        })),
      );

      if (result.optionId) {
        const gained = investigation.chooseOption(result.optionId);
        if (gained.length > 0) onClues(gained);
        setStatus('');
        heard.textContent = '';
      } else {
        // The one message that matters: it did not understand, and here are the options. Never
        // "try again" alone, which leaves a player who cannot be understood with nowhere to go.
        setStatus(t('transmit.unmatched'), 'warn');
      }
    } catch {
      // The proxy is unreachable. The conversation is not blocked by it.
      setStatus(t('transmit.offline'), 'warn');
    } finally {
      matching = false;
      startCountdown();
      if (pushToTalk) renderTransmitState(pushToTalk.state());
    }
  };

  const beginTransmit = (): void => {
    if (!pushToTalk || matching) return;
    heard.textContent = '';
    setStatus('');
    void pushToTalk.press().then(handleOutcome);
  };

  const endTransmit = (): void => {
    pushToTalk?.release();
  };

  if (pushToTalk) {
    // Hold with the pointer.
    talkButton.addEventListener('pointerdown', (event: PointerEvent) => {
      event.preventDefault();
      talkButton.setPointerCapture(event.pointerId);
      beginTransmit();
    });
    talkButton.addEventListener('pointerup', endTransmit);
    talkButton.addEventListener('pointercancel', endTransmit);
    // Leaving the window mid-transmission must not leave the key stuck down.
    window.addEventListener('blur', endTransmit);

    // Hold with the keyboard. Space is the button's own activation key, so it is handled here
    // rather than through click — a tap must not become a zero-length transmission.
    talkButton.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      if (!event.repeat) beginTransmit();
    });
    talkButton.addEventListener('keyup', (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      endTransmit();
    });
  }

  // Named once the conversation opens. Chapter 6 gives NPCs personas, and a panel headed
  // "Transmission" while Sigrid is talking wastes the only introduction she gets.
  const title = h('h2', { class: 'nb-dialogue__title', text: t('dialogue.title') });

  const element = h(
    'section',
    { class: 'nb-dialogue', 'aria-label': t('dialogue.title'), hidden: true },
    h(
      'header',
      { class: 'nb-dialogue__head' },
      title,
      h('button', {
        class: 'nb-button nb-button--quiet nb-button--small',
        type: 'button',
        text: t('dialogue.leave'),
        onclick: () => {
          pushToTalk?.reset();
          investigation.closeDialogue();
        },
      }),
    ),
    line,
    optionList,
    inputHint,
  );

  if (pushToTalk?.available && options.showVoiceRow !== false) {
    element.appendChild(voiceRow);
  }

  /* ---- rendering ------------------------------------------------------------------------- */

  const render = (view: InvestigationView): void => {
    const open = view.dialogue;
    element.hidden = open === null;
    if (!open) {
      speech?.stop();
      spokenNodeId = null;
      // The panel can now close without the player closing it — an NPC who roams away takes the
      // conversation with them. Release the key so it is not left held against nobody.
      pushToTalk?.reset();
      matching = false;
      heard.textContent = '';
      setStatus('');
      return;
    }

    title.textContent = open.label;
    line.textContent = open.node.line;

    if (open.node.id !== spokenNodeId) {
      spokenNodeId = open.node.id;
      // Fire and forget: a missing or unplayable clip is not a failure, and the line is already
      // on screen either way.
      if (open.node.clipUrl) void speech?.play(open.node.clipUrl);
      else speech?.stop();
    }

    const buttons = open.node.options.map((option, index) =>
      h(
        'button',
        {
          class: 'nb-button nb-button--primary nb-dialogue__option',
          type: 'button',
          onclick: () => {
            const gained = investigation.chooseOption(option.id);
            if (gained.length > 0) onClues(gained);
            setStatus('');
            heard.textContent = '';
          },
        },
        // A number per option: the keypress fallback, and a hint that clicking is expected.
        h('span', { class: 'nb-dialogue__key', text: String(index + 1) }),
        h('span', { text: option.label }),
      ),
    );

    if (buttons.length === 0) {
      optionList.replaceChildren(
        h('p', { class: 'nb-dialogue__ended', text: t('dialogue.ended') }),
      );
      voiceRow.hidden = true;
      inputHint.hidden = true;
      return;
    }

    voiceRow.hidden = false;
    inputHint.hidden = false;
    optionList.replaceChildren(...buttons);
  };

  /** Number keys pick an option — the keypress half of the non-voice fallback. */
  const onKeyDown = (event: KeyboardEvent): void => {
    if (element.hidden || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    const index = Number(event.key) - 1;
    const view = investigation.view.get().dialogue;
    if (!view || Number.isNaN(index)) return;
    const option = view.node.options[index];
    if (!option) return;

    event.preventDefault();
    const gained = investigation.chooseOption(option.id);
    if (gained.length > 0) onClues(gained);
  };

  window.addEventListener('keydown', onKeyDown);

  const unsubscribe = investigation.view.subscribe(render, true);
  if (pushToTalk) renderTransmitState(pushToTalk.state());

  return {
    element,
    dispose: () => {
      speech?.stop();
      unsubscribe();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', endTransmit);
      if (countdown !== null) clearInterval(countdown);
    },
  };
}
